import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, KeyRound, Network, Table2 } from "lucide-react";
import {
  api,
  type Schema,
  type SchemaTable,
  type SchemaView,
} from "@/lib/api";
import { ROUTES } from "@/routes";
import { EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const NODE_WIDTH = 292;
const NODE_GAP_X = 46;
const NODE_GAP_Y = 48;
const NODE_COLUMNS = 3;
const NODE_HEADER_HEIGHT = 44;
const NODE_ROW_HEIGHT = 24;
const MAX_VISIBLE_COLUMNS = 8;

export type SchemaRelation = {
  sourceTable: string;
  sourceColumns: string[];
  targetTable: string;
  targetColumns: string[];
  constraintName: string;
};

/** Extract the visualizer's table-to-table edges from PostgreSQL constraints. */
export function extractSchemaRelations(schema: Schema): SchemaRelation[] {
  const relations: SchemaRelation[] = [];
  const tableNames = new Set((schema.tables ?? []).map((table) => table.name));

  for (const table of schema.tables ?? []) {
    for (const constraint of table.constraints ?? []) {
      if (constraint.type !== "f" && !/foreign key/i.test(constraint.definition)) {
        continue;
      }
      const match = constraint.definition.match(
        /FOREIGN KEY\s*\(([^)]+)\)\s+REFERENCES\s+(.+?)\s*\(([^)]+)\)/i,
      );
      if (!match) continue;
      const targetTable = (match[2] ?? "")
        .trim()
        .split(".")
        .pop()
        ?.replace(/^"|"$/g, "");
      if (!targetTable || !tableNames.has(targetTable)) continue;
      relations.push({
        sourceTable: table.name,
        sourceColumns: splitConstraintColumns(match[1]),
        targetTable,
        targetColumns: splitConstraintColumns(match[3]),
        constraintName: constraint.name,
      });
    }
  }
  return relations;
}

function splitConstraintColumns(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim().replace(/^"|"$/g, ""))
    .filter(Boolean);
}

type VisualNode = {
  name: string;
  kind: "table" | "view";
  columns: SchemaTable["columns"] | SchemaView["columns"];
  table?: SchemaTable;
};

type PositionedNode = VisualNode & {
  x: number;
  y: number;
  height: number;
};

export function SchemaVisualizerPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [schema, setSchema] = useState<Schema | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    void api
      .getSchema(id)
      .then(({ data }) => {
        if (cancelled) return;
        setSchema({
          ...data,
          tables: data.tables ?? [],
          views: data.views ?? [],
          functions: data.functions ?? [],
          triggers: data.triggers ?? [],
        });
        setError(null);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const nodes = useMemo<VisualNode[]>(() => {
    if (!schema) return [];
    return [
      ...(schema.tables ?? []).map((table) => ({
        name: table.name,
        kind: "table" as const,
        columns: table.columns ?? [],
        table,
      })),
      ...(schema.views ?? []).map((view) => ({
        name: view.name,
        kind: "view" as const,
        columns: view.columns ?? [],
      })),
    ];
  }, [schema]);

  const relations = useMemo(
    () => (schema ? extractSchemaRelations(schema) : []),
    [schema],
  );
  const positioned = useMemo(() => positionNodes(nodes), [nodes]);
  const nodeByName = useMemo(
    () => new Map(positioned.map((node) => [node.name, node])),
    [positioned],
  );
  const graphWidth = NODE_COLUMNS * NODE_WIDTH + (NODE_COLUMNS + 1) * NODE_GAP_X;
  const graphHeight = positioned.length
    ? Math.max(...positioned.map((node) => node.y + node.height)) + NODE_GAP_Y
    : 320;

  if (loading && !schema) return <Spinner label="Loading schema visualizer…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Database"
        title="Schema Visualizer"
        description="See the project's tables, views, columns, and foreign-key relationships in one read-only map."
        actions={
          schema ? (
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{schema.tables.length} tables</Badge>
              <Badge variant="secondary">{schema.views.length} views</Badge>
              <Badge variant="secondary">{relations.length} relationships</Badge>
            </div>
          ) : null
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      {schema && nodes.length === 0 ? (
        <EmptyState title="No schema objects yet" icon={<Network className="h-5 w-5" />}>
          Create a table or view from the SQL editor to populate the visualizer.
        </EmptyState>
      ) : schema ? (
        <div className="space-y-6">
          <Card data-testid="schema-visualizer">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="font-sans text-base">Relationship map</CardTitle>
                  <CardDescription>
                    {schema.schema_name || "Project schema"} · scroll horizontally on narrow screens
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3 text-xs text-text-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-secondary" aria-hidden />
                    table
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-primary-container" aria-hidden />
                    view
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-sm border border-border-standard bg-background-subtle p-3">
                <svg
                  className="min-w-[760px]"
                  width={graphWidth}
                  height={graphHeight}
                  viewBox={`0 0 ${graphWidth} ${graphHeight}`}
                  role="img"
                  aria-label="Database schema relationship map"
                  data-testid="schema-visualizer-graph"
                >
                  <defs>
                    <marker
                      id="schema-relation-arrow"
                      markerWidth="8"
                      markerHeight="8"
                      refX="7"
                      refY="4"
                      orient="auto"
                    >
                      <path d="M0,0 L8,4 L0,8 z" fill="var(--color-secondary)" />
                    </marker>
                  </defs>
                  {relations.map((relation) => {
                    const source = nodeByName.get(relation.sourceTable);
                    const target = nodeByName.get(relation.targetTable);
                    if (!source || !target) return null;
                    const forward = target.x >= source.x;
                    const startX = forward ? source.x + NODE_WIDTH : source.x;
                    const endX = forward ? target.x : target.x + NODE_WIDTH;
                    const startY = source.y + NODE_HEADER_HEIGHT / 2;
                    const endY = target.y + NODE_HEADER_HEIGHT / 2;
                    const curve = Math.max(48, Math.abs(endX - startX) / 2);
                    return (
                      <path
                        key={`${relation.constraintName}-${relation.sourceTable}-${relation.targetTable}`}
                        d={`M ${startX} ${startY} C ${startX + (forward ? curve : -curve)} ${startY}, ${endX - (forward ? curve : -curve)} ${endY}, ${endX} ${endY}`}
                        fill="none"
                        stroke="var(--color-secondary)"
                        strokeOpacity="0.62"
                        strokeWidth="1.5"
                        markerEnd="url(#schema-relation-arrow)"
                      />
                    );
                  })}
                  {positioned.map((node) => (
                    <SchemaNode key={`${node.kind}-${node.name}`} node={node} />
                  ))}
                </svg>
              </div>
              {relations.length === 0 ? (
                <p className="mt-3 text-xs text-text-muted">
                  No foreign-key relationships were found. The map still lists every table and view.
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-sans text-base">Schema objects</CardTitle>
              <CardDescription>Open a table to inspect its columns and sample rows.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {schema.tables.map((table) => (
                  <Link
                    key={table.name}
                    to={ROUTES.databaseTable(id, table.name)}
                    className="group flex items-center justify-between gap-3 rounded-md border border-border-standard bg-surface-container-lowest px-3 py-3 transition-[color,background-color,border-color] duration-standard ease-standard hover:border-border-strong hover:bg-background-subtle"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Table2 className="h-4 w-4 shrink-0 text-secondary" aria-hidden />
                      <span className="truncate font-mono text-sm text-text-primary">{table.name}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-text-disabled transition-[color,transform] duration-standard ease-standard group-hover:translate-x-0.5 group-hover:text-secondary" aria-hidden />
                  </Link>
                ))}
                {schema.views.map((view) => (
                  <Link
                    key={view.name}
                    to={`/dashboard/projects/${id}/views/${encodeURIComponent(view.name)}`}
                    className="group flex items-center justify-between gap-3 rounded-md border border-border-standard bg-surface-container-lowest px-3 py-3 transition-[color,background-color,border-color] duration-standard ease-standard hover:border-border-strong hover:bg-background-subtle"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Network className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                      <span className="truncate font-mono text-sm text-text-primary">{view.name}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-text-disabled transition-[color,transform] duration-standard ease-standard group-hover:translate-x-0.5 group-hover:text-secondary" aria-hidden />
                  </Link>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={ROUTES.databaseTables(id)}>View all tables</Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={ROUTES.sql(id)}>Open SQL editor</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function positionNodes(nodes: VisualNode[]): PositionedNode[] {
  if (nodes.length === 0) return [];
  const rowHeights: number[] = [];
  for (let index = 0; index < nodes.length; index += NODE_COLUMNS) {
    rowHeights.push(
      Math.max(...nodes.slice(index, index + NODE_COLUMNS).map(nodeHeight)),
    );
  }
  return nodes.map((node, index) => {
    const row = Math.floor(index / NODE_COLUMNS);
    const column = index % NODE_COLUMNS;
    const y =
      NODE_GAP_Y +
      rowHeights.slice(0, row).reduce((total, height) => total + height + NODE_GAP_Y, 0);
    return {
      ...node,
      x: NODE_GAP_X + column * (NODE_WIDTH + NODE_GAP_X),
      y,
      height: nodeHeight(node),
    };
  });
}

function nodeHeight(node: VisualNode): number {
  return NODE_HEADER_HEIGHT +
    Math.max(1, Math.min(MAX_VISIBLE_COLUMNS, node.columns.length)) * NODE_ROW_HEIGHT +
    12;
}

function SchemaNode({ node }: { node: PositionedNode }) {
  const visibleColumns = node.columns.slice(0, MAX_VISIBLE_COLUMNS);
  const isView = node.kind === "view";
  return (
    <g data-testid={`schema-node-${node.name}`}>
      <rect
        x={node.x}
        y={node.y}
        width={NODE_WIDTH}
        height={node.height}
        rx="5"
        fill="var(--color-surface-container-lowest)"
        stroke="var(--color-border-standard)"
      />
      <rect
        x={node.x}
        y={node.y}
        width={NODE_WIDTH}
        height={NODE_HEADER_HEIGHT}
        rx="5"
        fill={isView ? "var(--color-primary-fixed)" : "var(--color-secondary-fixed)"}
      />
      <rect
        x={node.x}
        y={node.y + NODE_HEADER_HEIGHT - 5}
        width={NODE_WIDTH}
        height="5"
        fill={isView ? "var(--color-primary-fixed)" : "var(--color-secondary-fixed)"}
      />
      <text
        x={node.x + 14}
        y={node.y + 19}
        fill="var(--color-text-primary)"
        fontSize="13"
        fontWeight="700"
      >
        {node.name}
      </text>
      <text
        x={node.x + 14}
        y={node.y + 35}
        fill="var(--color-text-muted)"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        {isView ? "VIEW" : "TABLE"} · {node.columns.length} columns
      </text>
      {visibleColumns.map((column, index) => (
        <g key={column.name}>
          <text
            x={node.x + 14}
            y={node.y + NODE_HEADER_HEIGHT + 18 + index * NODE_ROW_HEIGHT}
            fill="var(--color-text-primary)"
            fontSize="11"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {column.name}
          </text>
          <text
            x={node.x + NODE_WIDTH - 14}
            y={node.y + NODE_HEADER_HEIGHT + 18 + index * NODE_ROW_HEIGHT}
            fill="var(--color-text-muted)"
            textAnchor="end"
            fontSize="10"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {column.is_primary_key ? "PK · " : ""}
            {column.udt_name || column.data_type}
          </text>
        </g>
      ))}
      {node.columns.length > MAX_VISIBLE_COLUMNS ? (
        <text
          x={node.x + 14}
          y={node.y + node.height - 8}
          fill="var(--color-text-muted)"
          fontSize="10"
        >
          +{node.columns.length - MAX_VISIBLE_COLUMNS} more columns
        </text>
      ) : null}
    </g>
  );
}
